import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { UserMenu } from './UserMenu';
import { OrganizationSwitcher } from '@/components/Settings/OrganizationSwitcher';
import { usePermissions } from '@/hooks/usePermissions';
import { appConfig, HOME_ROUTE } from '@/config/app';
import { useAuthStore } from '@/stores/authStore';
import { usePageHeaderStore } from '@/stores/pageHeaderStore';
import { queryClient } from '@/lib/queryClient';
import { useSocket } from '@/hooks/useSocket';
// VP Feedback Loop (AC-vpfb-7): notification bell in header
import { NotificationBell } from '@/components/roros/NotificationBell';

export interface NavItem {
  /** W2-G: accept a lazy function so t() fires at render time, not module load. */
  label: string | (() => string);
  href: string;
  icon: React.ReactNode;
  badge?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  external?: boolean; // Opens in new tab as a plain <a> link
  requiredPermissions?: string[]; // User must have ANY of these permissions
  /**
   * AC-BYOK-9: show a small amber attention dot next to the nav item when true.
   * Use for "something important is missing" signals (e.g. no AI provider configured).
   * attentionTitle is used for the title/aria attribute on the dot for accessibility (EN+DE).
   */
  attentionDot?: boolean;
  attentionTitle?: string;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
  requiredPermissions?: string[]; // Section visible if user has ANY of these permissions
}

export interface BaseLayoutProps {
  children: React.ReactNode;
  navItems?: NavItem[]; // Legacy support
  navSections?: NavSection[]; // New sectioned navigation
  sidebarTitle: string;
  headerSubtitle: string;
  rootPath: string;
  /** When true, main content fills the full remaining viewport height with no max-width wrapper */
  fullHeight?: boolean;
}

export function BaseLayout({
  children,
  navItems,
  navSections,
  sidebarTitle,
  headerSubtitle,
  rootPath,
  fullHeight = false,
}: BaseLayoutProps) {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
  const user = useAuthStore(s => s.user);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  const logoHref = isSuperAdmin ? '/admin' : HOME_ROUTE;
  const pageTitle = usePageHeaderStore(s => s.title);
  const pageIcon = usePageHeaderStore(s => s.icon);

  // v1.1.13 fix — initiate the socket.io connection. useSocketEvent only
  // SUBSCRIBES; useSocket() is the hook that actually opens the connection
  // when an authenticated user is in the auth store. Pre-fix, no component
  // called useSocket(), so the WebSocket was never established and every
  // useSocketEvent in the app was a no-op. This is why real-time refresh
  // (kanban, verification inbox, threshold alerts, etc.) appeared broken.
  // Mounting it on BaseLayout connects once per session — useSocket
  // ref-counts and dedupes the underlying socket instance.
  useSocket();

  // B12 — Auth-change listener: when user identity changes (login/logout/switch),
  // force a full permissions cache invalidation so the next render fetches fresh
  // data and the sidebar reflects the new user's access level.
  const userId = user?.id;
  useEffect(() => {
    // Invalidate all permission and 'me' queries when user changes.
    // Combined with the queryClient.clear() in resetSessionScopedState (called on
    // both login and logout), this provides belt-and-suspenders protection against
    // stale nav data persisting after a user switch.
    queryClient.invalidateQueries({ queryKey: ['permissions'] });
    queryClient.invalidateQueries({ queryKey: ['me'] });
  }, [userId]);

  // Close sidebar + scroll the main content back to the top on every route change.
  // BaseLayout puts html/body under `overflow: hidden` (see layout-locked below) and
  // makes <main> the scroll container, so window.scrollTo has no effect — we must
  // scroll the <main> element itself. Without this, navigating to pages like
  // /admin/design-system preserved the scroll position from the previous route.
  useEffect(() => {
    setIsSidebarOpen(false);
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Lock html/body scroll to prevent browser-level scrollbar (double scrollbar fix).
  // Only BaseLayout pages get this — public pages (landing, login) still use body scroll.
  useEffect(() => {
    document.documentElement.classList.add('layout-locked');
    return () => {
      document.documentElement.classList.remove('layout-locked');
    };
  }, []);

  // Convert legacy navItems to sections format for unified processing
  const sections = React.useMemo<NavSection[]>(() => {
    if (navSections) {
      return navSections;
    }
    if (navItems) {
      return [{ items: navItems }];
    }
    return [];
  }, [navItems, navSections]);

  // Filter sections and items based on permissions
  const filteredSections = React.useMemo(() => {
    return sections
      .map((section) => {
        // Filter items based on permissions
        const filteredItems = section.items.filter((item) => {
          if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
            return true;
          }
          // While permissions are loading, show items (will be filtered after load)
          if (permissionsLoading) {
            return true;
          }
          return hasAnyPermission(item.requiredPermissions as any[]);
        });

        return { ...section, items: filteredItems };
      })
      .filter((section) => {
        // Filter out empty sections
        if (section.items.length === 0) return false;
        
        // Check section-level permissions
        if (section.requiredPermissions && section.requiredPermissions.length > 0) {
          // While permissions are loading, show sections (will be filtered after load)
          if (permissionsLoading) {
            return true;
          }
          return hasAnyPermission(section.requiredPermissions as any[]);
        }
        
        return true;
      });
  }, [sections, hasAnyPermission, permissionsLoading]);

  // Flatten all items for active href detection
  const allItems = React.useMemo(() => {
    return filteredSections.flatMap((section) => section.items);
  }, [filteredSections]);

  // Determine the single active nav item by choosing the longest matching href prefix or exact match
  const activeHref = React.useMemo(() => {
    const matches = allItems.filter((item) => {
      if (item.href === rootPath) return location.pathname === rootPath;
      return location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    });
    if (matches.length === 0) return undefined as string | undefined;
    return matches.reduce((prev, cur) => (cur.href.length > prev.href.length ? cur : prev)).href;
  }, [location.pathname, allItems, rootPath]);

  return (
    <div className="h-dvh overflow-hidden bg-page-bg">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-40">
        {/* RR-0003: Switched from a 3-column grid (where the middle "center"
            column floated between the variable-width left/right zones) to a
            flex layout with the page title absolutely positioned. The title
            is now centered against the full header width regardless of how
            wide the logo (left) or org-switcher/user menu (right) are.
            `pointer-events-none` on the overlay ensures clicks still reach
            the underlying interactive zones.
            Top-bar breadcrumbs were removed globally (per product decision) —
            the page title in the centered overlay is the sole header label. */}
        <div className="relative flex items-center h-16 px-4 sm:px-6 lg:px-8 gap-4">
          {/* Left zone: mobile menu + logo */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to={logoHref} className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0">
              <img
                src="/images/logo.png"
                alt={appConfig.name}
                className="h-8 object-contain"
              />
            </Link>
          </div>

          {/* Right zone: org switcher + notification bell + user menu */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* AC-Global-1: OrganizationSwitcher now handles 0/1/N org cases
                internally — renders nothing for 0 orgs, a single-option
                dropdown for 1 org, and the full switcher for 2+ orgs. */}
            <OrganizationSwitcher />
            {/* VP Feedback Loop AC-vpfb-7: notification bell (renders nothing if no permission) */}
            <NotificationBell />
            <UserMenu />
          </div>

          {/* Center overlay: page icon + title — absolutely positioned so the
              title is centered on the full header width, not between the
              left/right zones. pointer-events-none lets clicks pass through
              to the breadcrumbs / org switcher / user menu underneath. */}
          {(pageTitle || pageIcon) && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 hidden sm:flex items-center justify-center px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-2 max-w-md min-w-0">
                {pageIcon && (
                  <span className="text-muted-foreground flex-shrink-0">{pageIcon}</span>
                )}
                {pageTitle && (
                  <span
                    className="text-sm font-semibold text-foreground truncate"
                    title={pageTitle}
                  >
                    {pageTitle}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="flex" style={{ height: 'calc(100dvh - 4rem)' }}>
        {/* Mobile backdrop */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar Navigation */}
        <nav 
          className={cn(
            "fixed lg:sticky top-16 z-40 h-[calc(100dvh-4rem)] w-72 bg-background border-r border-border transition-transform duration-200 ease-in-out lg:translate-x-0",
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="p-6 h-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {/* Mobile close button */}
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <h2 className="text-lg font-semibold text-foreground">{sidebarTitle}</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {filteredSections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Section Title (if provided) */}
                  {section.title && (
                    <div className="mb-2 px-3">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {section.title}
                      </h3>
                    </div>
                  )}

                  {/* Section Items */}
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const isActive = item.href === activeHref;
                      const isDisabled = item.comingSoon;
                      // W2-G: resolve label — supports both plain strings (boilerplate
                      // nav entries) and lazy () => t('…') functions (ROROS nav entries).
                      const itemLabel = typeof item.label === 'function' ? item.label() : item.label;

                      if (isDisabled) {
                        return (
                          <div
                            key={`${item.href}-${itemLabel}`}
                            className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-muted-foreground/60 cursor-not-allowed"
                          >
                            <span className="opacity-50">{item.icon}</span>
                            <span className="flex-1">{itemLabel}</span>
                            <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                              Coming Soon
                            </span>
                          </div>
                        );
                      }

                      if (item.external) {
                        return (
                          <a
                            key={`${item.href}-${itemLabel}`}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all text-muted-foreground hover:text-foreground hover:bg-muted"
                            onClick={() => setIsSidebarOpen(false)}
                          >
                            {item.icon}
                            <span className="flex-1">{itemLabel}</span>
                            <svg className="w-3.5 h-3.5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        );
                      }

                      return (
                        <Link
                          key={`${item.href}-${itemLabel}`}
                          to={item.href}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-all',
                            isActive
                              ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          )}
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          {item.icon}
                          <span className="flex-1">{itemLabel}</span>
                          {item.badge && (
                            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                              {item.badge}
                            </span>
                          )}
                          {item.attentionDot && (
                            <span
                              className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"
                              title={item.attentionTitle}
                              aria-label={item.attentionTitle}
                              data-testid="nav-attention-dot"
                            />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main
          ref={mainRef}
          className={cn('flex-1 min-w-0', fullHeight ? 'overflow-hidden' : 'overflow-y-auto')}
        >
          {fullHeight ? (
            // Full-height mode: no max-width wrapper, children fill the full area
            <div className="h-full">
              {children}
            </div>
          ) : (
            // Default mode: centered content with max-width, scrollable
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
